import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { post } from 'axios';
import { Button, Dimmer, Dropdown, Icon, Label, Loader, Table } from 'semantic-ui-react';
import { URL } from '../../../store/path';
import { setAlertModalState } from '../../common/data/actions';

const UPLOAD_MODES = {
    json: {
        option: 'Upload JSON Data',
        subtitle: 'Upload a JSON file to sync transaction data.',
        cardTitle: 'Upload JSON File',
        fileIcon: 'file code outline',
        chipIcon: 'file code',
        extensions: ['json'],
        accept: '.json,application/json',
        endpoint: '/upload/txn-upload',
    },
    missing: {
        option: 'Upload Missing Data',
        subtitle: 'Insert missing FDI transaction data from Excel, then post it via UTD.',
        cardTitle: 'Upload Missing Data (Excel)',
        fileIcon: 'file excel outline',
        chipIcon: 'file excel',
        extensions: ['xlsx'],
        accept: '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        endpoint: '/upload/txn-excel-upload',
    },
};

const modeOptions = Object.keys(UPLOAD_MODES).map(key => ({
    key,
    value: key,
    text: UPLOAD_MODES[key].option,
}));

const countLabels = { trans: 'Transactions', customer: 'Customers', charges: 'Charges' };

const UploadDataForm = () => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const [mode, setMode] = useState('json');
    const [fileObj, setFileObj] = useState(null);
    const [fileName, setFileName] = useState('');
    const [isWaiting, setWaiting] = useState(false);
    const [resultMsg, setResultMsg] = useState(null);
    const [uploadCounts, setUploadCounts] = useState(null);
    const [uploadErrors, setUploadErrors] = useState([]);

    const config = UPLOAD_MODES[mode];

    const showAlert = (type, title, msg) => {
        dispatch(setAlertModalState({ isOpen: true, type, size: 'mini', title, msg }));
    };

    const clearFile = () => {
        setFileObj(null);
        setFileName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const clearResults = () => {
        setResultMsg(null);
        setUploadCounts(null);
        setUploadErrors([]);
    };

    const changeMode = (e, { value }) => {
        if (value === mode) return;
        setMode(value);
        clearFile();
        clearResults();
    };

    const fileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const ext = file.name.split('.').pop().toLowerCase();
        if (!config.extensions.includes(ext)) {
            showAlert('error', 'Invalid File',
                `Only ${config.extensions.map(x => '.' + x).join(', ')} files are allowed to upload.`);
            e.target.value = '';
            return;
        }
        setFileObj(file);
        setFileName(file.name);
        clearResults();
    };

    const fileUpload = () => {
        const formData = new FormData();
        formData.append('file', fileObj);
        const reqConfig = {
            crossDomain: true,
            headers: {
                'X-RLB-DMS-ACC': localStorage.getItem('acc'),
                'Authorization': `Bearer ${localStorage.getItem('user')}`,
            },
        };
        return post(URL + config.endpoint, formData, reqConfig);
    };

    const onFormSubmit = async (e) => {
        e.preventDefault();
        if (!fileObj) {
            showAlert('error', 'No File', `Please select a ${config.extensions.join('/').toUpperCase()} file to upload.`);
            return;
        }
        setWaiting(true);
        clearResults();
        try {
            const response = await fileUpload();
            const data = response.data || {};
            if (data.type === 'error') {
                const errors = Array.isArray(data.errors) ? data.errors : [];
                setUploadErrors(errors);
                setResultMsg({
                    type: 'error',
                    text: errors.length
                        ? `${fileName}: ${data.msg || 'Validation failed'} (${data.error_count || errors.length} error(s))`
                        : `${fileName}: Uploaded with errors!`,
                });
                showAlert('error', 'Upload Error',
                    data.message || data.msg || (typeof data.data === 'string' ? data.data : '') || 'Upload failed.');
            } else {
                if (data.data && typeof data.data === 'object') setUploadCounts(data.data);
                setResultMsg({ type: 'success', text: `${fileName}: Successfully uploaded.` });
                showAlert('success', 'Success',
                    data.message || (typeof data.data === 'string' ? data.data : '') || data.msg || 'File uploaded successfully.');
                clearFile();
            }
        } catch (error) {
            const resData = error?.response?.data || {};
            if (Array.isArray(resData.errors)) setUploadErrors(resData.errors);
            const msg = resData.message
                || resData.msg
                || (typeof resData.data === 'string' ? resData.data : '')
                || error?.message
                || 'Upload failed.';
            setResultMsg({ type: 'error', text: `${fileName}: Upload failed.` });
            showAlert('error', 'Upload Error', msg);
        } finally {
            setWaiting(false);
        }
    };

    return (
        <>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 4px 12px',
            }}>
                <div>
                    <div style={{ fontSize: '17px', fontWeight: 600, color: '#1f2d3d', lineHeight: 1.2 }}>
                        Upload Data
                    </div>
                    <div style={{ fontSize: '12px', color: '#8a96a8', marginTop: '2px' }}>
                        {config.subtitle}
                    </div>
                </div>
                <Dropdown
                    selection
                    options={modeOptions}
                    value={mode}
                    onChange={changeMode}
                    disabled={isWaiting}
                    style={{ minWidth: '220px' }}
                />
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 16px' }}>
                <div style={{
                    width: '100%',
                    maxWidth: '520px',
                    background: '#ffffff',
                    border: '1px solid #eef1f5',
                    borderRadius: '12px',
                    padding: '28px 28px 22px',
                    boxShadow: '0 2px 10px rgba(33, 65, 112, 0.05)',
                    position: 'relative',
                }}>
                    {isWaiting && (
                        <Dimmer active inverted style={{ borderRadius: '12px' }}>
                            <Loader indeterminate>Processing File...</Loader>
                        </Dimmer>
                    )}

                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '14px',
                            background: '#e8f2fb',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '12px',
                        }}>
                            <Icon name={config.fileIcon} style={{ color: '#2185d0', fontSize: '24px', margin: 0 }} />
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2d3d' }}>
                            {config.cardTitle}
                        </div>
                        <div style={{ fontSize: '12.5px', color: '#8a96a8', marginTop: '4px' }}>
                            Only <strong>{config.extensions.map(x => '.' + x).join(', ')}</strong> files are supported.
                        </div>
                    </div>

                    <form onSubmit={onFormSubmit}>
                        <label
                            htmlFor='upload-data-file'
                            style={{
                                display: 'block',
                                border: '1.5px dashed #b4d4f0',
                                borderRadius: '10px',
                                background: '#f7fbff',
                                padding: '20px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'background 0.15s ease, border-color 0.15s ease',
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.background = '#eef4fb' }}
                            onMouseOut={(e) => { e.currentTarget.style.background = '#f7fbff' }}
                        >
                            <Icon name='cloud upload' style={{ color: '#2185d0', fontSize: '22px', marginBottom: '6px' }} />
                            <div style={{ fontSize: '13px', color: '#2185d0', fontWeight: 600 }}>
                                Click to choose a file
                            </div>
                            <div style={{ fontSize: '11.5px', color: '#8a96a8', marginTop: '2px' }}>
                                or drag and drop here
                            </div>
                        </label>
                        <input
                            id='upload-data-file'
                            ref={fileInputRef}
                            type='file'
                            accept={config.accept}
                            style={{ display: 'none' }}
                            onChange={fileChange}
                        />

                        {fileObj && (
                            <div style={{ marginTop: '14px', textAlign: 'center' }}>
                                <Label
                                    color='green'
                                    basic
                                    style={{
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Icon name={config.chipIcon} />
                                    <span title={fileName}>{fileName}</span>
                                    <Icon
                                        name='delete'
                                        link
                                        style={{ marginLeft: '6px' }}
                                        onClick={clearFile}
                                    />
                                </Label>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                            <Button
                                type='button'
                                basic
                                fluid
                                onClick={clearFile}
                                disabled={!fileObj || isWaiting}
                            >
                                Clear
                            </Button>
                            <Button
                                type='submit'
                                color='blue'
                                fluid
                                icon
                                labelPosition='left'
                                disabled={!fileObj || isWaiting}
                            >
                                <Icon name='upload' />
                                Upload
                            </Button>
                        </div>
                    </form>

                    {resultMsg && (
                        <div style={{
                            marginTop: '14px',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            background: resultMsg.type === 'success' ? '#e6f7eb' : '#fdecea',
                            color: resultMsg.type === 'success' ? '#1a7332' : '#c0392b',
                            border: `1px solid ${resultMsg.type === 'success' ? '#c4e8cf' : '#f5c6cb'}`,
                            fontSize: '12.5px',
                            textAlign: 'center',
                        }}>
                            <Icon name={resultMsg.type === 'success' ? 'check circle' : 'times circle'} />
                            {resultMsg.text}
                        </div>
                    )}

                    {uploadCounts && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                            {Object.keys(uploadCounts).map(key => (
                                <div key={key} style={{
                                    flex: 1,
                                    textAlign: 'center',
                                    padding: '10px 6px',
                                    borderRadius: '8px',
                                    background: '#f7fafc',
                                    border: '1px solid #eef1f5',
                                }}>
                                    <div style={{ fontSize: '18px', fontWeight: 600, color: '#1f2d3d' }}>
                                        {uploadCounts[key]}
                                    </div>
                                    <div style={{ fontSize: '11.5px', color: '#8a96a8', textTransform: 'uppercase' }}>
                                        {countLabels[key] || key}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {uploadErrors.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '0 16px 20px' }}>
                    <div style={{ width: '100%', maxWidth: '1000px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#c0392b', marginBottom: '8px' }}>
                            <Icon name='warning circle' />
                            Validation errors ({uploadErrors.length}) — nothing was uploaded. Fix the file and upload again.
                        </div>
                        <Table celled compact size='small'>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell collapsing>#</Table.HeaderCell>
                                    <Table.HeaderCell>Sheet</Table.HeaderCell>
                                    <Table.HeaderCell collapsing>Row</Table.HeaderCell>
                                    <Table.HeaderCell>Column</Table.HeaderCell>
                                    <Table.HeaderCell>Value</Table.HeaderCell>
                                    <Table.HeaderCell>Error</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {uploadErrors.map((err, i) => (
                                    <Table.Row key={i}>
                                        <Table.Cell>{i + 1}</Table.Cell>
                                        <Table.Cell>{err.sheet || '—'}</Table.Cell>
                                        <Table.Cell>{err.row != null ? err.row : '—'}</Table.Cell>
                                        <Table.Cell>{err.column || '—'}</Table.Cell>
                                        <Table.Cell>{err.value != null ? String(err.value) : '—'}</Table.Cell>
                                        <Table.Cell style={{ color: '#c0392b' }}>{err.error}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                </div>
            )}
        </>
    );
};

export default UploadDataForm;
